import {
  FormAnswersV2,
  FormCheckBoxField,
  FormInputType,
  FormRadioField,
  FormResponseV2,
  SavedFormQuestion,
} from "@markit/common.types";
import React, { useCallback, useMemo, useState } from "react";
import {
  getFileNameFromResponse,
  getFormResponseAnswers,
  questionInputName,
} from "../../utils/eventUtils/formUtils";
import { Colors } from "../../utils/colors";
import {
  formatCheckboxRadioAnswersLine,
  isFormResponsesOther,
  truncateString,
} from "@markit/common.utils";
import CustomLinkify from "../Links/CustomLinkify";
import { useTheme } from "../../hooks/useTheme";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import ThreeDotsButton from "../Buttons/ThreeDotsButton";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import SingleEditCustomData from "../FollowerProfile/SingleEditCustomData";
import { downloadMediaFromFirestore } from "../../utils/mediaUtils";

type FormResponseContainerProps = {
  eventId: string;
  item: SavedFormQuestion | undefined;
  clicked: boolean;
  onPress: () => void;
  individualUserResponse?: FormResponseV2;
  setIndividualUserResponse?: (individualUserResponse: FormResponseV2) => void;
  hideInactiveOptions?: boolean;
  containerStyles?: React.CSSProperties;
  /** When present, increments from FormsPanel (e.g. after an edit) to refetch all-users aggregate counts. */
  aggregateRefreshKey?: number;
};

export const FormResponseContainer = (props: FormResponseContainerProps) => {
  const {
    eventId,
    item,
    clicked,
    onPress,
    individualUserResponse,
    setIndividualUserResponse,
    hideInactiveOptions,
    containerStyles,
    aggregateRefreshKey,
  } = props;
  const { theme } = useTheme();

  const [responseAnswers, setResponseAnswers] = useState<FormAnswersV2[]>([]);
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [editResponseModal, setEditResponseModal] = useState(false);

  const styles = {
    subText: {
      color: theme.SubText.color,
      fontSize: 12,
    },
  };

  const handleFileDownload = useCallback(async () => {
    if (!item || !individualUserResponse?.answers[item.id]?.[0]) return;
    await downloadMediaFromFirestore(
      [individualUserResponse.answers[item.id][0]],
      fileName || item.formInput.label || "file"
    );
  }, [item, individualUserResponse, fileName]);

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Edit",
        icon: "ion:create",
        key: "edit_response",
        onPress: () => setEditResponseModal(true),
      },
    ],
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (!individualUserResponse) {
      // Gets the count of all the responses for a question for an event
      const formResponseAnswers = await getFormResponseAnswers(
        eventId,
        item ? item.id : ""
      );
      setResponseAnswers(formResponseAnswers);
    } else if (item) {
      // Check if the item is a file upload question
      if (item.formInput.inputType === FormInputType.FILE_UPLOAD) {
        const fileName = await getFileNameFromResponse(
          individualUserResponse.answers[item.id][0]
        );
        setFileName(fileName);
      }
    }
  }, [eventId, item, individualUserResponse, aggregateRefreshKey]);

  return (
    <>
      {item && individualUserResponse && editResponseModal ? (
        <SingleEditCustomData
          savedQuestion={item}
          userId={individualUserResponse.uid}
          eventFormResponse={individualUserResponse}
          setEventFormResponse={setIndividualUserResponse}
          eventId={eventId}
          setModalVisible={setEditResponseModal}
        />
      ) : null}
      <div
        className="ColumnNormal"
        style={{
          padding: "12px",
          borderRadius: "8px",
          backgroundColor: clicked ? Colors.GRAY6 : Colors.WHITE,
          cursor: "auto",
          ...containerStyles,
        }}
        onClick={onPress}
      >
        <div className="AlignedRowSpaced" style={{ gap: 14 }}>
          <div className="AlignedRow" style={{ gap: 7 }}>
            <span style={styles.subText}>
              {questionInputName(
                item ? item.formInput.inputType : FormInputType.EMAIL
              )}
            </span>
            {item && item.formInput.isRequired && (
              <span style={{ color: Colors.RED1, fontSize: "12px" }}>
                *Required
              </span>
            )}
          </div>
          {individualUserResponse ? (
            <DropdownMenu
              dropdownItems={dropdownItems}
              trigger={["click"]}
              placement="bottomRight"
              altWidth={150}
            >
              <ThreeDotsButton onPress={() => {}} />
            </DropdownMenu>
          ) : null}
        </div>
        <hr />
        <span
          style={{
            color: Colors.BLACK,
            fontSize: "14px",
            fontWeight: "600",
            marginBottom: "14px",
          }}
        >
          {item?.formInput.label}
        </span>
        <div className="ColumnNormal" style={{ gap: "14px" }}>
          {item?.formInput.inputType === FormInputType.TEXT_INPUT &&
            (individualUserResponse ? (
              <CustomLinkify>
                <span
                  style={{
                    ...(individualUserResponse.answers[item.id] &&
                    individualUserResponse.answers[item.id].every(
                      (response) => response === ""
                    )
                      ? theme.SubText
                      : theme.PrimaryText),
                    fontSize: "12px",
                  }}
                >
                  {(individualUserResponse.answers[item.id] &&
                    individualUserResponse.answers[item.id].every(
                      (response) => response === ""
                    )) ||
                  individualUserResponse.answers[item.id] === undefined
                    ? "No Response Collected"
                    : individualUserResponse.answers[item.id].join(", ")}
                </span>
              </CustomLinkify>
            ) : (
              <span style={styles.subText}>
                {item.formInput.placeholder || "Answer here..."}
              </span>
            ))}
          {item?.formInput.inputType === FormInputType.CHECK_BOX ||
          item?.formInput.inputType === FormInputType.RADIO
            ? (() => {
                const input = item.formInput as
                  | FormCheckBoxField
                  | FormRadioField;
                const listedActive = input.options ?? [];
                const listedInactive = hideInactiveOptions
                  ? []
                  : (input.inactiveOptions ?? []);
                const hasOtherOption = Boolean(input.hasOtherOption);
                const displayRows = [
                  ...listedActive,
                  ...listedInactive,
                  ...(hasOtherOption ? ["Other"] : []),
                ];
                return displayRows.map((option: string, index: number) => {
                  const answers = individualUserResponse?.answers[item.id];
                  const otherAnswers = (answers ?? []).filter(
                    (answer) =>
                      !listedActive.includes(answer) &&
                      !listedInactive.includes(answer)
                  );
                  const optionLabel =
                    option === "Other"
                      ? formatCheckboxRadioAnswersLine(
                          otherAnswers,
                          listedActive,
                          listedInactive,
                          "Other"
                        )
                      : formatCheckboxRadioAnswersLine(
                          [option],
                          listedActive,
                          listedInactive
                        );
                  const isSelected =
                    individualUserResponse !== undefined &&
                    answers &&
                    isFormResponsesOther(
                      option,
                      answers,
                      listedActive,
                      listedInactive
                    );
                  return (
                    <div
                      key={`${option}-${index}`}
                      style={{
                        marginBottom: 5,
                        display: "flex",
                        alignItems: "center",
                        cursor: "auto",
                      }}
                    >
                      {isSelected ? (
                        <span
                          style={{
                            color: Colors.BLACK,
                            fontSize: "12px",
                            marginRight: 10,
                          }}
                        >
                          Selected
                        </span>
                      ) : individualUserResponse === undefined ? (
                        <span
                          style={{
                            color: Colors.BLACK,
                            fontSize: "12px",
                            marginRight: "7px",
                          }}
                        >
                          {responseAnswers.reduce(
                            (prev, curr) =>
                              prev +
                              (curr[item.id] &&
                              isFormResponsesOther(
                                option,
                                curr[item.id],
                                listedActive,
                                listedInactive
                              )
                                ? 1
                                : 0),
                            0
                          )}
                        </span>
                      ) : null}
                      <span
                        style={{
                          color: isSelected ? Colors.BLACK : Colors.GRAY1,
                          fontSize: "12px",
                        }}
                      >
                        {truncateString(optionLabel, 60, "...")}
                      </span>
                    </div>
                  );
                });
              })()
            : null}
          {item?.formInput.inputType === FormInputType.EMAIL &&
            (individualUserResponse ? (
              <CustomLinkify>
                <span
                  style={{
                    ...(individualUserResponse.answers[item.id] &&
                    individualUserResponse.answers[item.id].every(
                      (response) => response === ""
                    )
                      ? theme.SubText
                      : theme.PrimaryText),
                    fontSize: "12px",
                  }}
                >
                  {(individualUserResponse.answers[item.id] &&
                    individualUserResponse.answers[item.id].every(
                      (response) => response === ""
                    )) ||
                  individualUserResponse.answers[item.id] === undefined
                    ? "No Response Collected"
                    : individualUserResponse.answers[item.id].join(", ")}
                </span>
              </CustomLinkify>
            ) : (
              <div style={{ fontSize: "12px" }}>Enter email here...</div>
            ))}
          {item?.formInput.inputType === FormInputType.FILE_UPLOAD && (
            <span
              style={{
                ...(!fileName ? theme.SubText : theme.PrimaryText),
                fontSize: "12px",
              }}
            >
              {individualUserResponse ? (
                fileName ? (
                  <span
                    style={{ color: Colors.BLUE5, cursor: "pointer" }}
                    onClick={handleFileDownload}
                  >
                    {fileName}
                  </span>
                ) : (
                  "No Response Collected"
                )
              ) : (
                "Upload File"
              )}
            </span>
          )}
        </div>
      </div>
    </>
  );
};
